<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Genel bakış"
      title="Operasyon merkezi"
      description="Vite referansındaki operasyon paneli hissini koruyarak kullanıcı, kampanya, finans ve sistem sinyallerini tek dashboard yüzeyinde topladık."
    />

    <v-alert
      v-if="loadError"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ loadError }}
    </v-alert>

    <admin-metric-strip
      :items="compactStats"
      :loading="loading"
    />
    <section
      class="dashboard-work-queue"
      aria-label="İş kuyrukları"
    >
      <router-link :to="{name: 'cashTransfer'}">
        Bekleyen ödemeler
      </router-link>
      <router-link :to="{name: 'settlements'}">
        Mutabakat
      </router-link>
      <template v-if="canAccessUserList">
        <router-link :to="{name: 'campaignsList', query: {tab: 'pending'}}">
          Onay bekleyen kampanyalar
        </router-link>
        <router-link :to="{name: 'contactRequests'}">
          İletişim talepleri
        </router-link>
        <router-link :to="{name: 'whatsappSupport'}">
          Destek gelen kutusu
        </router-link>
        <router-link :to="{name: 'cronMonitor', query: {attention: '1'}}">
          Hatalı işler
        </router-link>
      </template>
      <span
        v-if="loadedAt"
        class="text-caption"
      >Güncellendi: {{ loadedAt }}</span>
      <v-btn
        text
        small
        :loading="loading"
        @click="loadDashboard"
      >
        Yenile
      </v-btn>
    </section>
    <div
      v-if="loaded"
      class="admin-dashboard-grid"
    >
      <v-card
        class="admin-dashboard-card admin-dashboard-span-8"
        outlined
      >
        <v-card-title class="admin-dashboard-card__head">
          <div>
            <span class="admin-dashboard-card__eyebrow">Performans</span>
            <h3 class="admin-dashboard-card__title">
              Kampanya performansı
            </h3>
          </div>
          <v-chip
            small
            outlined
            color="primary"
          >
            Son 30 gün
          </v-chip>
        </v-card-title>

        <v-card-text class="admin-dashboard-card__content">
          <v-sparkline
            :value="campaignPerformance"
            color="info"
            fill
            auto-draw
            line-width="3"
            padding="10"
            height="168"
            smooth
            class="admin-dashboard-chart"
          />

          <div class="admin-dashboard-pill-grid">
            <div
              v-for="metric in performanceMetrics"
              :key="metric.label"
              class="admin-dashboard-pill"
            >
              <strong>{{ metric.value }}</strong>
              <span>{{ metric.label }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="admin-dashboard-card admin-dashboard-span-4"
        outlined
      >
        <v-card-title class="admin-dashboard-card__head">
          <div>
            <span class="admin-dashboard-card__eyebrow">Trend</span>
            <h3 class="admin-dashboard-card__title">
              Kullanıcı kayıt akışı
            </h3>
          </div>
          <v-chip
            small
            outlined
            color="info"
          >
            Haftalık
          </v-chip>
        </v-card-title>

        <v-card-text class="admin-dashboard-card__content">
          <div class="admin-dashboard-bars">
            <div
              v-for="item in registrationData"
              :key="item.label"
              class="admin-dashboard-bars__item"
            >
              <div class="admin-dashboard-bars__track">
                <span
                  class="admin-dashboard-bars__fill"
                  :style="registrationBarStyle(item.value)"
                ></span>
              </div>
              <span class="admin-dashboard-bars__label">{{ item.label }}</span>
            </div>
          </div>

          <div class="admin-dashboard-mini-list">
            <div
              v-for="item in registrationData"
              :key="`${item.label}-summary`"
              class="admin-dashboard-mini-list__row"
            >
              <span>{{ item.label }}</span>
              <strong>{{ item.value }} kayıt</strong>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="admin-dashboard-card admin-dashboard-span-6"
        outlined
      >
        <v-card-title class="admin-dashboard-card__head">
          <div>
            <span class="admin-dashboard-card__eyebrow">Finans</span>
            <h3 class="admin-dashboard-card__title">
              Son para çekim istekleri
            </h3>
          </div>
          <v-btn
            text
            color="primary"
            small
            :to="{ name: 'cashTransfer' }"
          >
            Tümünü Gör
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-0">
          <v-simple-table class="admin-dashboard-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Kullanıcı</th>
                  <th>Tutar</th>
                  <th>Banka</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="withdrawal in recentWithdrawals"
                  :key="withdrawal.id"
                >
                  <td>{{ withdrawal.user }}</td>
                  <td class="font-weight-bold">
                    {{ withdrawal.amount }}
                  </td>
                  <td>{{ withdrawal.bank }}</td>
                  <td>
                    <span
                      class="admin-dashboard-badge"
                      :style="{ color: withdrawal.color, backgroundColor: withdrawal.softColor }"
                    >
                      {{ withdrawal.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <v-card
        class="admin-dashboard-card admin-dashboard-span-6"
        outlined
      >
        <v-card-title class="admin-dashboard-card__head">
          <div>
            <span class="admin-dashboard-card__eyebrow">Leaderboard</span>
            <h3 class="admin-dashboard-card__title">
              Top 10 kullanıcı
            </h3>
          </div>
          <v-btn
            text
            color="primary"
            small
            :to="{ name: 'userList' }"
          >
            Kullanıcı Listesi
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-0">
          <div class="admin-dashboard-leaderboard">
            <div
              v-for="user in topUsers"
              :key="user.rank"
              class="admin-dashboard-leaderboard__row"
            >
              <span
                class="admin-dashboard-rank"
                :class="`admin-dashboard-rank--${Math.min(user.rank, 4)}`"
              >
                {{ user.rank }}
              </span>

              <div
                class="admin-dashboard-avatar"
                :style="{ backgroundColor: user.softColor, color: user.color }"
              >
                {{ user.initials }}
              </div>

              <div class="admin-dashboard-leaderboard__copy">
                <strong>{{ user.name }}</strong>
                <span>{{ user.points }}</span>
              </div>

              <span class="admin-dashboard-leaderboard__earnings">
                {{ user.earnings }}
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="admin-dashboard-card admin-dashboard-span-12"
        outlined
      >
        <v-card-title class="admin-dashboard-card__head">
          <div>
            <span class="admin-dashboard-card__eyebrow">Sistem</span>
            <h3 class="admin-dashboard-card__title">
              Sistem durumu
            </h3>
          </div>
        </v-card-title>

        <v-card-text class="admin-dashboard-system">
          <div
            v-for="item in systemStatus"
            :key="item.label"
            class="admin-dashboard-system__item"
          >
            <span
              class="admin-dashboard-system__dot"
              :style="{ backgroundColor: item.color }"
            ></span>
            <span class="admin-dashboard-system__label">{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiAccountGroupOutline,
  mdiAlertCircleOutline,
  mdiBullhorn,
  mdiCash,
  mdiChevronDown,
  mdiLinkVariant,
  mdiTrendingUp,
} from '@mdi/js'
import {
  getAccessToken,
  getPanelSession,
  hasAdminAccess,
} from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'

const KPI_STYLES = [
  {
    title: 'Toplam Kullanıcılar',
    icon: mdiAccountGroupOutline,
    trendIcon: mdiTrendingUp,
    iconColor: '#0d6efd',
    trendColor: 'success',
    trendTextColor: '#56CA00',
    softColor: 'rgba(13, 110, 253, 0.14)',
    glow: 'radial-gradient(circle at center, rgba(13, 110, 253, 0.22) 0%, rgba(13, 110, 253, 0) 72%)',
    helper: 'Son 30 gunde yeni kullanici',
  },
  {
    title: 'Aktif Kampanyalar',
    icon: mdiBullhorn,
    trendIcon: mdiTrendingUp,
    iconColor: '#56CA00',
    trendColor: 'success',
    trendTextColor: '#56CA00',
    softColor: 'rgba(86, 202, 0, 0.14)',
    glow: 'radial-gradient(circle at center, rgba(86, 202, 0, 0.2) 0%, rgba(86, 202, 0, 0) 72%)',
    helper: 'Son 30 gunde acilan kampanya',
  },
  {
    title: 'Bekleyen Para Cekimleri',
    icon: mdiCash,
    trendIcon: mdiChevronDown,
    iconColor: '#FFB400',
    trendColor: 'warning',
    trendTextColor: '#FFB400',
    softColor: 'rgba(255, 180, 0, 0.14)',
    glow: 'radial-gradient(circle at center, rgba(255, 180, 0, 0.22) 0%, rgba(255, 180, 0, 0) 72%)',
    helper: 'Finans incelemesi bekliyor',
  },
  {
    title: 'Bugun Olusturulan Linkler',
    icon: mdiLinkVariant,
    trendIcon: mdiTrendingUp,
    iconColor: '#d41028',
    trendColor: 'primary',
    trendTextColor: '#d41028',
    softColor: 'rgba(212, 16, 40, 0.14)',
    glow: 'radial-gradient(circle at center, rgba(212, 16, 40, 0.22) 0%, rgba(212, 16, 40, 0) 72%)',
    helper: 'Son 30 gun link akisi',
  },
  {
    title: 'Mutabakat Açığı',
    icon: mdiAlertCircleOutline,
    trendIcon: mdiChevronDown,
    iconColor: '#FF4C51',
    trendColor: 'error',
    trendTextColor: '#FF4C51',
    softColor: 'rgba(255, 76, 81, 0.14)',
    glow: 'radial-gradient(circle at center, rgba(255, 76, 81, 0.2) 0%, rgba(255, 76, 81, 0) 72%)',
    helper: 'Detay için açık bakiyeli kullanıcıları göster',
  },
]

const DASHBOARD_COLORS = [
  { color: '#FFB400', softColor: 'rgba(255, 180, 0, 0.14)' },
  { color: '#0d6efd', softColor: 'rgba(13, 110, 253, 0.14)' },
  { color: '#56CA00', softColor: 'rgba(86, 202, 0, 0.14)' },
  { color: '#8A8D93', softColor: 'rgba(138, 141, 147, 0.16)' },
  { color: '#d41028', softColor: 'rgba(212, 16, 40, 0.14)' },
]

export default {
  name: 'DashboardPage',
  components: {
    AdminPageHeader,
    AdminMetricStrip,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      loadedAt: '',
      loadError: '',
      stats: [],
      campaignPerformance: [],
      performanceMetrics: [],
      registrationData: [],
      recentWithdrawals: [],
      topUsers: [],
      systemStatus: [],
    }
  },
  computed: {
    compactStats() {
      return this.stats.map(item => ({
        label: item.title, value: item.value, helper: `${item.trend} · ${item.helper}`, to: item.to,
      }))
    },
    requestOptions() {
      const token = getAccessToken()

      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    },
    canAccessUserList() {
      return hasAdminAccess(getPanelSession())
    },
    maxRegistrationValue() {
      return Math.max(...this.registrationData.map(item => item.value), 1)
    },
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      this.loadError = ''

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}panel/dashboard`,
          this.requestOptions,
        )

        const payload = response?.data?.data || response?.data?.body || response?.data || {}
        this.applyDashboard(payload)
        this.loaded = true
        this.loadedAt = new Date().toLocaleTimeString('tr-TR', { timeZone: 'Europe/Istanbul', hour: '2-digit', minute: '2-digit' })
      } catch (error) {
        console.error('Dashboard yukleme hatasi:', error)
        this.loadError = this.loaded ? 'Veriler yenilenemedi. Son başarılı yükleme gösteriliyor.' : 'Dashboard verileri yüklenemedi. Yeniden deneyin.'
      } finally {
        this.loading = false
      }
    },
    applyDashboard(payload) {
      const campaignSeries = this.normalizeSeries(payload.campaignPerformance, 12)
      const registrationData = this.normalizeRegistrationData(payload.registrationData)
      const withdrawalRows = this.normalizeWithdrawals(payload.recentWithdrawals)
      const topUsers = this.normalizeTopUsers(payload.topUsers)

      this.campaignPerformance = campaignSeries
      this.registrationData = registrationData
      this.performanceMetrics = [
        { label: 'Link uretimi', value: this.formatCompact(payload.linksLast30Days) },
        { label: 'Tiklama', value: this.formatCompact(payload.clicksLast30Days) },
        { label: 'Donusum', value: this.formatCompact(payload.conversionsLast30Days) },
      ]
      this.recentWithdrawals = withdrawalRows
      this.topUsers = topUsers
      this.systemStatus = this.normalizeSystemStatus(payload.systemStatus)
      this.stats = this.buildStats(payload, campaignSeries, registrationData, withdrawalRows)
    },
    buildStats(payload, campaignSeries, registrationData, withdrawalRows) {
      const registrationSeries = registrationData.map(item => Number(item.value || 0))
      const withdrawalSeries = withdrawalRows.map(item => Number(item.amountValue || 0))

      return [
        {
          ...KPI_STYLES[0],
          value: this.formatCompact(payload.totalUsers),
          trend: `+${this.formatCompact(payload.recentUsers)}`,
          series: this.ensureSeries(registrationSeries, 8),
        },
        {
          ...KPI_STYLES[1],
          value: this.formatCompact(payload.activeCampaigns),
          trend: `${this.formatCompact(payload.newCampaigns)} yeni`,
          series: this.ensureSeries(campaignSeries, 8),
        },
        {
          ...KPI_STYLES[2],
          value: this.formatCurrency(payload.pendingWithdrawAmount),
          trend: `${this.formatCompact(payload.pendingWithdrawCount)} istek`,
          series: this.ensureSeries(withdrawalSeries, 8),
        },
        {
          ...KPI_STYLES[3],
          value: this.formatCompact(payload.linksToday),
          trend: `${this.formatCompact(payload.linksLast30Days)} / 30g`,
          series: this.ensureSeries(campaignSeries.slice(-8), 8),
        },
        {
          ...KPI_STYLES[4],
          helper: this.canAccessUserList
            ? KPI_STYLES[4].helper
            : 'Toplam mutabakat açığı',
          value: `${this.formatCompact(payload.reconciliationDeficitUserCount)} kullanıcı`,
          trend: this.formatCurrency(payload.reconciliationDeficitTotalAmount),
          series: this.ensureSeries([Number(payload.reconciliationDeficitUserCount || 0)], 8),
          to: this.canAccessUserList
            ? {
              name: 'userList',
              query: { reconciliationDeficit: 'true' },
            }
            : null,
        },
      ]
    },
    normalizeSeries(series, expectedLength) {
      const values = Array.isArray(series) ? series.map(item => Number(item || 0)) : []

      return this.ensureSeries(values, expectedLength)
    },
    ensureSeries(values, expectedLength) {
      const safeValues = Array.isArray(values) ? values.filter(value => Number.isFinite(Number(value))) : []
      if (!safeValues.length) {
        return Array.from({ length: expectedLength }, () => 0)
      }

      const normalized = safeValues.slice(-expectedLength)
      while (normalized.length < expectedLength) {
        normalized.unshift(0)
      }

      return normalized
    },
    normalizeRegistrationData(items) {
      if (!Array.isArray(items) || !items.length) {
        return []
      }

      return items.map((item, index) => ({
        label: item.label || `Hft ${index + 1}`,
        value: Number(item.value || 0),
      }))
    },
    normalizeWithdrawals(items) {
      if (!Array.isArray(items)) return []

      return items.map(item => {
        const status = this.resolveWithdrawalStatus(item.status)
        const amountValue = Number(item.amount || 0)

        return {
          id: item.id,
          user: item.user || '-',
          amount: this.formatCurrency(item.amount),
          amountValue,
          bank: item.bank || '-',
          status: status.label,
          color: status.color,
          softColor: status.softColor,
        }
      })
    },
    normalizeTopUsers(items) {
      if (!Array.isArray(items)) return []

      return items.map((item, index) => {
        const palette = DASHBOARD_COLORS[index % DASHBOARD_COLORS.length]

        return {
          rank: Number(item.rank || index + 1),
          initials: this.buildInitials(item.name),
          name: item.name || '-',
          points: `${this.formatCompact(item.points)} puan`,
          earnings: this.formatCurrency(item.earnings),
          color: palette.color,
          softColor: palette.softColor,
        }
      })
    },
    normalizeSystemStatus(items) {
      if (!Array.isArray(items) || !items.length) {
        return this.systemStatus
      }

      return items.map(item => ({
        label: item.label,
        value: item.value,
        color: item.color || '#56CA00',
      }))
    },
    resolveWithdrawalStatus(status) {
      const normalized = String(status || 'PENDING').toUpperCase()
      if (normalized === 'SUCCESS') {
        return {
          label: 'Tamamlandi',
          color: '#56CA00',
          softColor: 'rgba(86, 202, 0, 0.16)',
        }
      }
      if (normalized === 'FAILED') {
        return {
          label: 'Reddedildi',
          color: '#FF4C51',
          softColor: 'rgba(255, 76, 81, 0.16)',
        }
      }

      return {
        label: 'Bekleyen',
        color: '#FFB400',
        softColor: 'rgba(255, 180, 0, 0.16)',
      }
    },
    buildInitials(name) {
      const parts = String(name || '')
        .split(' ')
        .map(item => item.trim())
        .filter(Boolean)
        .slice(0, 2)

      if (!parts.length) return '--'

      return parts.map(item => item.charAt(0).toUpperCase()).join('')
    },
    formatCompact(value) {
      const number = Number(value || 0)
      if (number >= 1000000) {
        return `${(number / 1000000).toFixed(1)}M`
      }
      if (number >= 1000) {
        return `${(number / 1000).toFixed(1)}K`
      }

      return `${Math.round(number)}`
    },
    formatCurrency(value) {
      const amount = Number(value || 0)

      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount)
    },
    registrationBarStyle(value) {
      const ratio = (value / this.maxRegistrationValue) * 100

      return {
        height: `${Math.max(ratio, 12)}%`,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-work-queue { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 16px 0; border-block: 1px solid #d5d8dd; }
.admin-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
}

.admin-dashboard-span-12 {
  grid-column: span 12;
}

.admin-dashboard-span-8 {
  grid-column: span 8;
}

.admin-dashboard-span-6 {
  grid-column: span 6;
}

.admin-dashboard-span-4,
.admin-dashboard-kpi {
  grid-column: span 4;
}

.admin-dashboard-kpi__body {
  position: relative;
  overflow: hidden;
  padding: 20px !important;
}

.admin-dashboard-kpi--clickable {
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.admin-dashboard-kpi--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(30, 39, 53, 0.12) !important;
}

.admin-dashboard-kpi__glow {
  position: absolute;
  top: -42px;
  right: -18px;
  width: 130px;
  height: 130px;
  pointer-events: none;
}

.admin-dashboard-kpi__top {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.admin-dashboard-kpi__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.admin-dashboard-kpi__label,
.admin-dashboard-card__eyebrow,
.admin-dashboard-bars__label {
  color: rgba(148, 163, 184, 0.9);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-dashboard-kpi__value {
  font-size: 30px;
  line-height: 1;
  letter-spacing: -0.03em;
}

.admin-dashboard-kpi__trend {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
}

.admin-dashboard-kpi__helper {
  color: rgba(148, 163, 184, 0.88);
  font-size: 12px;
}

.admin-dashboard-kpi__sparkline {
  margin-top: 18px;
}

.admin-dashboard-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 0 !important;
}

.admin-dashboard-card__title {
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.admin-dashboard-card__content {
  padding-top: 12px !important;
}

.admin-dashboard-chart {
  border-radius: 18px;
}

.admin-dashboard-pill-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.admin-dashboard-pill {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(148, 163, 184, 0.08);
}

.admin-dashboard-pill strong {
  font-size: 18px;
}

.admin-dashboard-pill span,
.admin-dashboard-mini-list__row span,
.admin-dashboard-leaderboard__copy span,
.admin-dashboard-system__label {
  color: rgba(148, 163, 184, 0.9);
  font-size: 12px;
}

.admin-dashboard-bars {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(52px, 1fr));
  gap: 10px;
  align-items: end;
  height: 190px;
}

.admin-dashboard-bars__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 0;
  text-align: center;
}

.admin-dashboard-bars__label { color: var(--admin-text); letter-spacing: 0; overflow-wrap: anywhere; }

.admin-dashboard-bars__track {
  width: 100%;
  height: 150px;
  display: flex;
  align-items: flex-end;
  padding: 8px;
  border-radius: 6px;
  background: rgba(148, 163, 184, 0.08);
}

.admin-dashboard-bars__fill {
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(180deg, rgba(13, 110, 253, 0.95) 0%, rgba(13, 110, 253, 0.55) 100%);
  box-shadow: 0 12px 22px -18px rgba(13, 110, 253, 0.8);
}

.admin-dashboard-mini-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
}

.admin-dashboard-mini-list__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.admin-dashboard-table {
  background: transparent !important;
}

.admin-dashboard-table ::v-deep table {
  width: 100%;
}

.admin-dashboard-table ::v-deep th {
  color: rgba(148, 163, 184, 0.9);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-bottom-color: rgba(148, 163, 184, 0.12) !important;
}

.admin-dashboard-table ::v-deep td {
  border-bottom-color: rgba(148, 163, 184, 0.12) !important;
}

.admin-dashboard-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.admin-dashboard-leaderboard {
  display: flex;
  flex-direction: column;
}

.admin-dashboard-leaderboard__row {
  display: grid;
  grid-template-columns: 28px 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.admin-dashboard-leaderboard__row:last-child {
  border-bottom: 0;
}

.admin-dashboard-rank,
.admin-dashboard-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.admin-dashboard-rank {
  width: 28px;
  height: 28px;
}

.admin-dashboard-avatar {
  width: 36px;
  height: 36px;
}

.admin-dashboard-rank--1 {
  background: #ffd700;
  color: #111827;
}

.admin-dashboard-rank--2 {
  background: #c0c0c0;
  color: #111827;
}

.admin-dashboard-rank--3 {
  background: #cd7f32;
  color: #fff;
}

.admin-dashboard-rank--4 {
  background: rgba(148, 163, 184, 0.16);
  color: rgba(248, 250, 252, 0.86);
}

.admin-dashboard-leaderboard__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.admin-dashboard-leaderboard__copy strong {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-dashboard-leaderboard__earnings {
  color: #56CA00;
  font-size: 12px;
  font-weight: 700;
}

.admin-dashboard-system {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding-top: 12px !important;
}

.admin-dashboard-system__item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.08);
}

.admin-dashboard-system__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}

@media (max-width: 1264px) {
  .admin-dashboard-kpi,
  .admin-dashboard-span-4,
  .admin-dashboard-span-6,
  .admin-dashboard-span-8,
  .admin-dashboard-span-12 {
    grid-column: span 12;
  }
}

@media (max-width: 960px) {
  .admin-dashboard-grid {
    gap: 14px;
  }

  .admin-dashboard-pill-grid {
    grid-template-columns: 1fr;
  }

  .admin-dashboard-bars {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    height: auto;
  }

  .admin-dashboard-card__head {
    flex-direction: column;
    align-items: flex-start;
  }

  .admin-dashboard-leaderboard__row {
    grid-template-columns: 28px 36px minmax(0, 1fr);
  }

  .admin-dashboard-leaderboard__earnings {
    grid-column: 2 / -1;
    padding-left: 4px;
  }

  .admin-dashboard-system {
    flex-direction: column;
  }
}
</style>
